<template>
  <div class="h-screen w-screen overflow-hidden bg-[var(--bg-primary)] text-[var(--text-primary)]">
    <div class="flex h-full flex-col md:flex-row">
      <!-- Sidebar -->
      <aside class="w-full shrink-0 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]/60 backdrop-blur md:h-full md:w-[320px] md:border-b-0 md:border-r">
        <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <button
            class="inline-flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-white/10"
            @click="createNewSession"
          >
            <span class="text-lg leading-none">+</span>
            <span>Nova conversa</span>
          </button>
          <router-link
            to="/dashboard"
            class="rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-white/10"
            title="Voltar ao Dashboard"
          >
            Voltar
          </router-link>
        </div>

        <div class="max-h-[34vh] overflow-y-auto px-2 pb-4 md:h-[calc(100vh-72px)] md:max-h-none">
          <div v-if="loadingChats" class="px-3 py-2 text-sm text-[var(--text-secondary)]">Carregando conversas…</div>
          <div v-else>
            <template v-for="group in groupedSessions" :key="group.label">
              <div class="px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                {{ group.label }}
              </div>
              <div class="mt-2 space-y-1">
                <button
                  v-for="s in group.items"
                  :key="s.sessionId"
                  class="w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-white/10"
                  :class="s.sessionId === activeSessionId ? 'bg-white/10' : ''"
                  @click="openSession(s.sessionId)"
                >
                  <div class="truncate font-medium">
                    {{ s.title || `Sessão ${s.sessionId.slice(0, 8)}` }}
                  </div>
                  <div class="truncate text-xs text-[var(--text-secondary)]">
                    Atualizada: {{ formatShort(s.updatedAt) }}
                  </div>
                </button>
              </div>
            </template>

            <div v-if="groupedSessions.length === 0" class="px-3 py-3 text-sm text-[var(--text-secondary)]">
              Nenhuma conversa ainda.
            </div>
          </div>
        </div>
      </aside>

      <!-- Main -->
      <main class="relative flex h-full min-w-0 flex-1 flex-col">
        <!-- Messages -->
        <div ref="scrollEl" class="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          <div class="mx-auto w-full max-w-3xl">
            <div v-if="messages.length === 0" class="flex min-h-[60vh] flex-col items-center justify-center gap-6">
              <div class="flex flex-col items-center gap-3 text-center">
                <img src="/logo.png" alt="Logo" class="h-14 w-14 rounded-2xl object-contain bg-white/5 p-2" />
                <div>
                  <div class="text-xl font-semibold">Assistente de IA</div>
                  <div class="text-sm text-[var(--text-secondary)]">
                    Produtividade e análise de dados para o seu SaaS.
                  </div>
                </div>
              </div>

              <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  v-for="s in suggestions"
                  :key="s.title"
                  class="rounded-xl border border-[var(--border-color)] bg-white/5 p-4 text-left hover:bg-white/10"
                  @click="applySuggestion(s.prompt)"
                >
                  <div class="font-semibold">{{ s.title }}</div>
                  <div class="mt-1 text-sm text-[var(--text-secondary)]">{{ s.description }}</div>
                </button>
              </div>
            </div>

            <div v-else class="space-y-6">
              <div v-for="m in messages" :key="m.id" class="flex gap-3">
                <div
                  class="mt-1 h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-xs font-bold"
                  :class="m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'"
                >
                  {{ m.role === 'user' ? 'EU' : 'IA' }}
                </div>

                <div class="min-w-0 flex-1">
                  <div class="rounded-2xl border border-[var(--border-color)] bg-white/5 px-4 py-3">
                    <MarkdownMessage v-if="m.role !== 'user'" :content="m.content" />
                    <div v-else class="whitespace-pre-wrap text-sm leading-relaxed">{{ m.content }}</div>
                  </div>
                </div>
              </div>

              <div v-if="isThinking" class="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
                <span class="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-400"></span>
                <span>A IA está pensando…</span>
              </div>

              <div v-if="errorMessage" class="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
                {{ errorMessage }}
              </div>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="border-t border-[var(--border-color)] bg-[var(--bg-primary)]/80 backdrop-blur">
          <div class="mx-auto w-full max-w-3xl px-4 py-4 sm:px-6">
            <div class="relative">
              <textarea
                v-model="draft"
                rows="1"
                placeholder="Digite uma mensagem…"
                class="w-full resize-none rounded-2xl border border-[var(--border-color)] bg-white/5 px-4 py-3 pr-24 text-sm leading-relaxed outline-none focus:border-blue-500/60 sm:pr-12"
                @keydown.enter.exact.prevent="onSend"
                @input="autoGrow"
                ref="textareaEl"
              />
              <button
                class="absolute bottom-2 right-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2"
                :disabled="!canSend"
                @click="onSend"
              >
                Enviar
              </button>
            </div>
            <div class="mt-2 text-xs text-[var(--text-secondary)]">
              Enter para enviar • Shift+Enter para quebrar linha
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue';
import MarkdownMessage from '@/modules/ai-chat/components/MarkdownMessage.vue';
import { chatAiService } from '@/modules/ai-chat/services/chatAi.service';
import type { ChatSession } from '@/modules/ai-chat/types/chat';
import { useChatStreaming } from '@/modules/ai-chat/composables/useChatStreaming';

type SessionGroup = { label: string; items: ChatSession[] };

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function groupSessions(sessions: ChatSession[]): SessionGroup[] {
  const now = new Date();
  const today = startOfDay(now);
  const yesterday = today - 24 * 60 * 60 * 1000;

  const buckets: Record<string, ChatSession[]> = {
    Hoje: [],
    Ontem: [],
    'Últimos 7 dias': [],
    'Mais antigas': [],
  };

  for (const s of sessions) {
    const t = startOfDay(new Date(s.updatedAt || s.createdAt));
    if (t >= today) buckets.Hoje.push(s);
    else if (t >= yesterday) buckets.Ontem.push(s);
    else if (t >= today - 7 * 24 * 60 * 60 * 1000) buckets['Últimos 7 dias'].push(s);
    else buckets['Mais antigas'].push(s);
  }

  const order = ['Hoje', 'Ontem', 'Últimos 7 dias', 'Mais antigas'];
  return order
    .map((label) => ({ label, items: buckets[label] }))
    .filter((g) => g.items.length > 0);
}

function safeRandomUUID(): string {
  const anyCrypto = globalThis.crypto as Crypto | undefined;
  return anyCrypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default defineComponent({
  name: 'ChatDashboard',
  components: { MarkdownMessage },
  setup() {
    const sessions = ref<ChatSession[]>([]);
    const loadingChats = ref(false);

    const draft = ref('');
    const textareaEl = ref<HTMLTextAreaElement | null>(null);
    const scrollEl = ref<HTMLElement | null>(null);

    const {
      messages,
      activeSessionId,
      isThinking,
      errorMessage,
      setMessages,
      sendUserMessage,
    } = useChatStreaming();

    const suggestions = [
      { title: 'Analise minhas vendas', description: 'Tendências, sazonalidade e insights.', prompt: 'Analise minhas vendas do último mês e destaque tendências e anomalias.' },
      { title: 'Resumo do estoque', description: 'Itens críticos e recomendações.', prompt: 'Faça um resumo do estoque e aponte itens com risco de ruptura.' },
      { title: 'Previsão de caixa', description: 'Cenários e próximos 30 dias.', prompt: 'Crie uma previsão de fluxo de caixa para os próximos 30 dias com cenários.' },
      { title: 'Diagnóstico de clientes', description: 'Segmentação e oportunidades.', prompt: 'Segmentar clientes por valor e frequência e sugerir ações.' },
    ];

    const groupedSessions = computed(() => groupSessions([...sessions.value].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))));
    const canSend = computed(() => !!activeSessionId.value && draft.value.trim().length > 0 && !isThinking.value);

    function formatShort(iso: string) {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    }

    async function refreshChats() {
      loadingChats.value = true;
      try {
        const res = await chatAiService.listChats();
        sessions.value = res.chats || [];
      } finally {
        loadingChats.value = false;
      }
    }

    async function openSession(sessionId: string) {
      activeSessionId.value = sessionId;
      const history = await chatAiService.getChatHistory(sessionId);
      setMessages(history.messages || []);
      await nextTick();
      scrollToBottom();
    }

    async function createNewSession() {
      const sessionId = safeRandomUUID();
      activeSessionId.value = sessionId;
      setMessages([]);
      // Opcionalmente já adiciona na sidebar como “local draft”
      const now = new Date().toISOString();
      sessions.value = [
        { sessionId, title: 'Nova conversa', createdAt: now, updatedAt: now },
        ...sessions.value,
      ];
      await nextTick();
      textareaEl.value?.focus();
    }

    function scrollToBottom() {
      const el = scrollEl.value;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }

    function autoGrow() {
      const el = textareaEl.value;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
    }

    function applySuggestion(prompt: string) {
      draft.value = prompt;
      nextTick(() => {
        autoGrow();
        textareaEl.value?.focus();
      });
    }

    async function onSend(e?: KeyboardEvent) {
      // Shift+Enter: permitir quebra de linha
      if (e?.shiftKey) return;
      if (!activeSessionId.value) {
        await createNewSession();
      }
      const sessionId = activeSessionId.value as string;
      const content = draft.value.trim();
      if (!content) return;

      draft.value = '';
      nextTick(() => autoGrow());

      await sendUserMessage({
        sessionId,
        content,
        runRequest: (signal) =>
          chatAiService.sendMessageStream(
            { sessionId, message: content },
            signal
          ),
      });

      // Atualiza “updatedAt” localmente (a lista oficial vem do backend via refresh)
      const now = new Date().toISOString();
      sessions.value = sessions.value.map((s) => (s.sessionId === sessionId ? { ...s, updatedAt: now } : s));

      await nextTick();
      scrollToBottom();
    }

    onMounted(async () => {
      await refreshChats();
      if (sessions.value.length > 0) {
        await openSession(sessions.value[0].sessionId);
      } else {
        await createNewSession();
      }
    });

    return {
      loadingChats,
      groupedSessions,
      activeSessionId,
      messages,
      isThinking,
      errorMessage,
      draft,
      suggestions,
      textareaEl,
      scrollEl,
      formatShort,
      openSession,
      createNewSession,
      applySuggestion,
      autoGrow,
      onSend,
      canSend,
    };
  },
});
</script>

